import { Select } from 'antd';
import { Modal } from 'antd';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import Typo from 'consts/typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { selectAssets, selectCoins } from 'features/home/homeSlice';
import { IMG_ALT } from 'consts/app';
import SuccessIcon from 'assets/icons/SuccessIcon';
import useUserDetail from 'hooks/useUserDetail';
import useThemes from 'hooks/useThemes';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/userSlice';
import { isValidAmount } from './WithdrawModal';
import { openPopup } from 'utils/commonUtils';
import VinpayConnectModal from './VinpayConnectModal';
import vinpayApi from 'api/vinpayApi';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import CustomAmountButton from 'components/Commons/CustomAmountButton';
import { showConfirmModal } from 'components/Modals/CommonModals';
import InfomationIcon from 'assets/icons/InfomationIcon';
import { WalletInfoVinpayResponse } from 'models/vinpay';
import { formatAmountV2 } from 'utils/formatAmount';
import { AssetResponseModel } from 'models/asset';
import DepositQr from './DepositQr';
import OnramperModal from './OnramperModal';

interface VinpayDepositModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
}

const VinpayDepositModal: React.FunctionComponent<VinpayDepositModalProps> = (props) => {
  const { title = '', visible, closeModal, className = '' } = props;
  const t = useAppTranslation();

  const [currentAsset, setCurrentAsset] = useState<number>(0);
  const [currentInfoAsset, setCurrentInfoAsset] = useState<AssetResponseModel | null>();
  const [currentCoin, setCurrentCoin] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const [isOpenVinpayConnectModal, setIsOpenVinpayConnectModal] = useState<boolean>(false);
  const [vinpayWallet, setVinpayWallet] = useState<WalletInfoVinpayResponse>();
  const [onramperLink, setOnramperLink] = useState<string | null>(null);

  const coins = useAppSelector(selectCoins);
  const assets = useAppSelector(selectAssets);
  const currentUser = useSelector(selectCurrentUser);

  const { isUserLoggedIn } = useUserDetail();
  const { currentTheme } = useThemes();
  const { isPhone, isTablet } = useAppSelector(platformStates);

  const handleResponseError = useHandleResponseError();

  const coinOptions = useMemo(() => {
    const priorityCoins = ['usdt', '$tbet', 'pyusd'];
    return coins
      .filter((x) => x.enableDeposit)
      .sort((a, b) => {
        const aPriority = priorityCoins.includes(a.symbol.toLowerCase()) ? 0 : 1;
        const bPriority = priorityCoins.includes(b.symbol.toLowerCase()) ? 0 : 1;

        if (aPriority !== bPriority) return aPriority - bPriority;

        return 0;
      })
      .map((coin) => ({
        value: coin.id,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={coin.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {coin.symbol}
            </span>
          </div>
        ),
        symbol: coin.symbol,
        minAmount: coin.minAmount,
        maxAmount: coin.maxAmount,
      }));
  }, [coins, isPhone, isTablet]);

  const currentCrypto = useMemo(() => {
    const cr = coinOptions.find((c) => c.value === currentCoin);

    return cr;
  }, [currentCoin, coinOptions]);

  const networkOptions = useMemo(() => {
    return assets
      .filter((asset) => asset.coin.id === currentCoin)
      .map((asset) => ({
        value: asset.assetId,
        label: (
          <span
            className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
          >
            {asset.chain.name}
          </span>
        ),
      }));
  }, [assets, currentCoin, isPhone, isTablet]);

  useEffect(() => {
    if (coins.length !== 0) {
      setCurrentCoin(coins.find((coin) => coin.enableDeposit && coin.id === 4)?.id || 0);
    }
  }, [coins]);

  useEffect(() => {
    if (assets.length !== 0) {
      setCurrentAsset(assets.find((asset) => asset.coin.id === currentCoin)?.assetId || 0);
      setCurrentInfoAsset(assets.find((asset) => asset.coin.id === currentCoin) || null);
    }
  }, [currentCoin, assets]);

  const getVinpayWallet = async () => {
    const { ok, body } = await vinpayApi.getVinpayWallet();
    if (ok && body) {
      setVinpayWallet(body);
    }
  };

  useEffect(() => {
    if (visible) {
      getVinpayWallet();
    }
  }, [visible]);

  const balanceAddress = useMemo(() => {
    if (vinpayWallet) {
      const currentWallet = currentInfoAsset
        ? vinpayWallet.cryptoWallet.find(
            (c) =>
              c.chainSymbol === currentInfoAsset.chain.symbol &&
              c.coinSymbol === currentInfoAsset.coin.symbol,
          )
        : 0;
      return currentWallet ? currentWallet.availableBalance : 0;
    }
    return 0;
  }, [vinpayWallet, currentInfoAsset, visible]);

  const onAssetChange = (event: number) => {
    setCurrentAsset(event);
    setCurrentInfoAsset(assets.find((a) => a.assetId === event) || null);
  };
  const onCoinChange = (event: number) => {
    setCurrentCoin(event);
  };

  const handleVinpayConnectClick = () => {
    setIsOpenVinpayConnectModal(!isOpenVinpayConnectModal);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*[.,]?[0-9]{0,8}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const onSubmit = useCallback(async () => {
    setIsLoading(true);
    if (parseFloat(amount) <= balanceAddress) {
      const { ok, error } = await vinpayApi.createTransaction({
        amount: Number(amount),
        isCrypto: true,
        type: 'DEPOSIT',
        assetId: currentAsset,
      });
      if (ok) {
        setIsShowSuccess(true);
      }
      if (error) handleResponseError(error);
    } else {
      handleDeposit();
    }
    setIsLoading(false);
  }, [amount, balanceAddress, currentAsset]);

  const onCloseModal = () => {
    resetModal();
    closeModal();
  };

  const resetModal = () => {
    setAmount('');
    setIsShowSuccess(false);
  };

  const isValid = useMemo(() => {
    return !isLoading && Number(amount) !== 0;
  }, [balanceAddress, amount, isLoading]);

  const handleSetField = (value: string) => {
    setAmount(value);
  };

  const handleDoubleClickOption = useCallback(
    (value: string) => {
      showConfirmModal({
        content: (
          <p>
            {t('confirmModal.deposit.message', {
              amount: `${value} ${currentCrypto?.symbol}`,
            })}
          </p>
        ),
        width: 360,
        icon: <InfomationIcon />,
        onOk: onSubmit,
        okText: 'Submit',
        cancelText: 'Cancel',
      });
    },
    [amount],
  );

  const showOnramper = (url: string) => {
    Modal.destroyAll();
    setOnramperLink(url);
  };

  const handleDepositPaypal = async () => {
    const { ok, body, error } = await vinpayApi.paypalDeposit({
      amount: parseFloat(amount),
      chainSymbol: currentInfoAsset?.chain.symbol,
    });
    if (ok && body) {
      openPopup(body.approveLink);
      setAmount('');
    }
    if (error) {
      handleResponseError(error);
    }
    Modal.destroyAll();
  };

  const cryptoDeposit = async () => {
    if (currentInfoAsset) {
      const { ok, body, error } = await vinpayApi.getVinpayAddress(currentInfoAsset.assetId, {
        amount: parseFloat(amount),
      });
      if (ok && body) {
        showConfirmModal({
          className: 'info-modal-qr-deposit',
          title: 'Deposit',
          content: (
            <DepositQr
              address={body.depositAddress.address}
              symbol={body.depositAddress.chainDto.symbol}
              tag={body.depositAddress.tag}
            />
          ),
          okText: t('vinpay.connectModal.buyCrypto'),
          onOk: () =>
            currentInfoAsset.coin.symbol === '$TBET'
              ? handleDepositPaypal()
              : showOnramper(body.onramperLink),
          icon: null,
          closeIcon: <CloseIcon width="20" height="20" isDarkMode={currentTheme === 'dark'} />,
          closable: true,
        });
        setAmount('');
      }
      if (error) {
        handleResponseError(error);
      }
    }
  };

  const handleDeposit = async () => {
    cryptoDeposit();
  };
  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {!isShowSuccess ? (
        <div className="center-content-col gap-3">
          <div className="common-select">
            <p className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
              {t('cashier.depositOption')}
            </p>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentCoin}
              onChange={onCoinChange}
              options={coinOptions}
            />
          </div>
          <div className="common-select">
            <p className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
              {t('cashier.network')}
            </p>
            <Select
              className="antd__select"
              dropdownClassName="antd__select-dropdown"
              value={currentAsset}
              onChange={onAssetChange}
              options={networkOptions}
            />
          </div>
          <div className="common-input input-amount">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('cashier.amount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              onChange={onChange}
              value={amount}
              placeholder={t('placeholder.enterAmount')}
            />
          </div>
          {/* <div className="common-input input-amount w-100 ">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            ></label>
            <CustomAmountButton
              symbol={currentCrypto?.symbol}
              onSetField={handleSetField}
              onOptionDoubleClick={handleDoubleClickOption}
              classes="custom-button-swap custom-button-modal"
            />
          </div> */}
          <div className="withdraw-balance">
            <div className="withdraw-balance__left"></div>
            <div className="withdraw-balance__right">
              <p
                className={`${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2} font-normal`}
              >{`${t('balance')} (Vinpay): ${formatAmountV2(balanceAddress, 8)}`}</p>
            </div>
          </div>
          {isUserLoggedIn && (
            <div className="withdraw-balance">
              <div className="withdraw-balance__left"></div>
              <div className="withdraw-balance__right">
                <button
                  disabled={currentUser?.isVinpayConnected && !isValid}
                  className={`button-generate ${
                    currentUser?.isVinpayConnected && !isValid ? 'disabled' : ''
                  }`}
                  type="submit"
                  onClick={currentUser?.isVinpayConnected ? onSubmit : handleVinpayConnectClick}
                >
                  {currentUser?.isVinpayConnected ? t('deposit.title') : t('common.connectVinpay')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="center-content-col gap-3 withdraw--success text-center">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('deposit.success.title')}</p>
          <p className="message">{`$${amount} ${t('deposit.success.message')}`}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('deposit.close')}
          </button>
        </div>
      )}
      <VinpayConnectModal
        visible={isOpenVinpayConnectModal}
        closeModal={handleVinpayConnectClick}
        className="common-modal withdraw-modal"
      />
      {!!onramperLink && (
        <OnramperModal
          isShow={!!onramperLink}
          onCancel={() => setOnramperLink(null)}
          onramperLink={onramperLink}
        />
      )}
    </Modal>
  );
};

export default VinpayDepositModal;
